/**
 * Date and Time Formatter with dynamic regional settings support
 * Uses settings from session storage (loaded from company settings)
 */

// Default date/time settings (fallback)
const DEFAULT_DATE_SETTINGS = {
  dateFormat: 'dd/MM/yyyy',
  timeFormat: '24', // '12' or '24'
  timezone: 'Asia/Kolkata'
};

// Try to load from session storage on initialization
const loadSettingsFromStorage = () => {
  try {
    const stored = sessionStorage.getItem('date_format');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Could not load date format settings from storage:', error);
  }
  return { ...DEFAULT_DATE_SETTINGS };
};

// Global date settings (loaded from company settings or session storage)
let globalDateSettings = loadSettingsFromStorage();

/**
 * Set global date settings from company settings
 * @param {Object} settings - Date format settings from backend
 */
export const setDateFormatSettings = (settings) => {
  console.log('📅 [DATE FORMATTER] setDateFormatSettings called with:', settings);
  
  globalDateSettings = {
    dateFormat: settings.dateFormat || DEFAULT_DATE_SETTINGS.dateFormat,
    timeFormat: settings.timeFormat || DEFAULT_DATE_SETTINGS.timeFormat,
    timezone: settings.timezone || DEFAULT_DATE_SETTINGS.timezone
  };
  
  console.log('📅 [DATE FORMATTER] New settings applied:', globalDateSettings);
  
  // Also save to session storage
  try {
    sessionStorage.setItem('date_format', JSON.stringify(globalDateSettings));
    console.log('📅 [DATE FORMATTER] Settings saved to session storage');
  } catch (error) {
    console.warn('📅 [DATE FORMATTER] Could not save to session storage:', error);
  }
};

/**
 * Get current date format settings
 * @returns {Object} Current date format settings
 */
export const getDateFormatSettings = () => {
  // Reload from storage to ensure we have latest
  globalDateSettings = loadSettingsFromStorage();
  return { ...globalDateSettings };
};

/**
 * Convert date format pattern to date-fns format
 * @param {string} format - Format pattern (e.g., 'MM/dd/yyyy', 'dd-MM-yyyy')
 * @returns {string} date-fns format pattern
 */
const convertDateFormat = (format) => {
  const formatMap = {
    'MM/dd/yyyy': 'MM/dd/yyyy',
    'dd/MM/yyyy': 'dd/MM/yyyy',
    'yyyy-MM-dd': 'yyyy-MM-dd',
    'dd-MM-yyyy': 'dd-MM-yyyy',
    'MMM dd, yyyy': 'MMM dd, yyyy',
    'dd MMM yyyy': 'dd MMM yyyy'
  };
  return formatMap[format] || format;
};

/**
 * Parse date value from various formats (array, string, Date object)
 * Backend sends LocalDateTime as array [y,m,d,h,mi,s] or ISO string, in store/server local time.
 * We treat datetime arrays as local time (no UTC conversion) so the stored time displays as-is.
 * @param {any} dateValue - Date value in various formats
 * @returns {Date|null} Parsed Date object or null
 */
const parseDateValue = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // Handle array format from backend: [year, month, day, hour, minute, second]
    // Backend LocalDateTime is in store/server local time (e.g. IST). Use as local time so 10:20 shows 10:20.
    if (Array.isArray(dateValue)) {
      if (dateValue.length >= 6) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
        return new Date(Date.UTC(year, month - 1, day, hour, minute, second || 0));
      } else if (dateValue.length >= 3) {
        const [year, month, day] = dateValue;
        return new Date(year, month - 1, day);
      }
      return null;
    }
    
    // Handle string format
    if (typeof dateValue === 'string') {
      // Handle MySQL DATETIME format: "YYYY-MM-DD HH:mm:ss"
      if (dateValue.includes(' ') && !dateValue.includes('T')) {
        return new Date(dateValue.replace(' ', 'T'));
      }
      // Handle ISO format: "YYYY-MM-DDTHH:mm:ss" or "YYYY-MM-DDTHH:mm:ss.SSSZ"
      if (dateValue.includes('T')) {
        return new Date(dateValue);
      }
      // Handle date only: "YYYY-MM-DD"
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return new Date(dateValue + 'T00:00:00');
      }
      // Try parsing as is
      return new Date(dateValue);
    }
    
    // Handle Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    return null;
  } catch (error) {
    console.warn('Error parsing date value:', dateValue, error);
    return null;
  }
};

/**
 * Format date using regional settings
 * @param {any} dateValue - Date value in various formats
 * @param {Object} options - Override options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateValue, options = {}) => {
  if (!dateValue) return '-';
  
  const settings = { ...globalDateSettings, ...options };
  const date = parseDateValue(dateValue);
  
  if (!date || isNaN(date.getTime())) {
    console.warn('Invalid date value:', dateValue);
    return '-';
  }
  
  try {
    // Use date-fns for formatting
    const { format } = require('date-fns');
    const formatPattern = convertDateFormat(settings.dateFormat);
    return format(date, formatPattern);
  } catch (error) {
    // If date-fns format doesn't match, try fallback
    try {
      return formatDateFallback(date, settings.dateFormat);
    } catch (fallbackError) {
      console.warn('Error formatting date, using default:', fallbackError);
      // Ultimate fallback
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  }
};

/**
 * Format time using regional settings
 * @param {any} dateValue - Date value in various formats
 * @param {Object} options - Override options
 * @returns {string} Formatted time string
 */
export const formatTime = (dateValue, options = {}) => {
  if (!dateValue) return '-';
  
  const settings = { ...globalDateSettings, ...options };
  const date = parseDateValue(dateValue);
  
  if (!date || isNaN(date.getTime())) {
    return '-';
  }
  
  try {
    const is24Hour = settings.timeFormat === '24' || settings.timeFormat === '24h';
    
    if (is24Hour) {
      // 24-hour format: HH:mm
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } else {
      // 12-hour format: hh:mm AM/PM
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  } catch (error) {
    console.warn('Error formatting time:', error);
    return '-';
  }
};

/**
 * Format date and time together using regional settings
 * @param {any} dateValue - Date value in various formats
 * @param {Object} options - Override options
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateValue, options = {}) => {
  if (!dateValue) return '-';
  
  const dateStr = formatDate(dateValue, options);
  const timeStr = formatTime(dateValue, options);
  
  if (dateStr === '-' && timeStr === '-') return '-';
  if (dateStr === '-') return timeStr;
  if (timeStr === '-') return dateStr;
  
  return `${dateStr} ${timeStr}`;
};

/**
 * Fallback date formatting when date-fns is not available
 * @param {Date} date - Date object
 * @param {string} format - Format pattern
 * @returns {string} Formatted date string
 */
const formatDateFallback = (date, format) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = monthNames[date.getMonth()];
  
  switch (format) {
    case 'MM/dd/yyyy':
      return `${month}/${day}/${year}`;
    case 'dd/MM/yyyy':
      return `${day}/${month}/${year}`;
    case 'yyyy-MM-dd':
      return `${year}-${month}-${day}`;
    case 'dd-MM-yyyy':
      return `${day}-${month}-${year}`;
    case 'MMM dd, yyyy':
      return `${monthName} ${day}, ${year}`;
    case 'dd MMM yyyy':
      return `${day} ${monthName} ${year}`;
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Format date for display in tables (short format)
 * @param {any} dateValue - Date value in various formats
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateValue) => {
  return formatDate(dateValue);
};

/**
 * Format date and time for display in tables
 * @param {any} dateValue - Date value in various formats
 * @returns {string} Formatted date and time string
 */
export const formatDateWithTime = (dateValue) => {
  return formatDateTime(dateValue);
};

export default {
  formatDate,
  formatTime,
  formatDateTime,
  formatDateShort,
  formatDateWithTime,
  setDateFormatSettings,
  getDateFormatSettings
};
